import { ValidationRule } from '@/types/common';

export const rules = {
  required: (message = 'This field is required'): ValidationRule<unknown> => ({
    validate: (value: unknown) => value != null && value !== '',
    message
  }),

  min: (min: number, message = `Must be at least ${min}`): ValidationRule<number> => ({
    validate: (value) => value >= min,
    message
  }),

  max: (max: number, message = `Must be at most ${max}`): ValidationRule<number> => ({
    validate: (value) => value <= max,
    message
  }),

  email: (message = 'Invalid email address'): ValidationRule<string> => ({
    validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message
  }),

  pattern: (regex: RegExp, message: string): ValidationRule<string> => ({
    validate: (value) => regex.test(value),
    message
  })
};

/** Password validation rules for signup forms */
export const passwordRules = {
  minLength: (min = 8, message = `Password must be at least ${min} characters`): ValidationRule<string> => ({
    validate: (value) => value.length >= min,
    message
  }),

  hasUppercase: (message = 'Password must contain at least one uppercase letter'): ValidationRule<string> => ({
    validate: (value) => /[A-Z]/.test(value),
    message
  }),

  hasLowercase: (message = 'Password must contain at least one lowercase letter'): ValidationRule<string> => ({
    validate: (value) => /[a-z]/.test(value),
    message
  }),

  hasNumber: (message = 'Password must contain at least one number'): ValidationRule<string> => ({
    validate: (value) => /\d/.test(value),
    message
  }),

  hasSpecialChar: (message = 'Password must contain at least one special character'): ValidationRule<string> => ({
    validate: (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
    message
  }),

  /** Validates password against all strength requirements */
  validateAll: (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!passwordRules.minLength().validate(password)) {
      errors.push(passwordRules.minLength().message);
    }
    if (!passwordRules.hasUppercase().validate(password)) {
      errors.push(passwordRules.hasUppercase().message);
    }
    if (!passwordRules.hasLowercase().validate(password)) {
      errors.push(passwordRules.hasLowercase().message);
    }
    if (!passwordRules.hasNumber().validate(password)) {
      errors.push(passwordRules.hasNumber().message);
    }
    if (!passwordRules.hasSpecialChar().validate(password)) {
      errors.push(passwordRules.hasSpecialChar().message);
    }

    return { isValid: errors.length === 0, errors };
  }
};